import { CustomPriceModel } from '../../../shared/ui-models/unit-type.ui-model';

/**
 * Direction of a percentage adjustment applied to dynamic (custom) prices.
 */
export enum PriceAdjustmentDirection {
  increase = 'increase',
  decrease = 'decrease'
}

/**
 * How a resulting dynamic price row relates to what was there before the adjustment.
 */
export enum PriceAdjustmentChangeType {
  /** An existing dynamic price whose days fall inside the selected period. */
  adjusted = 'adjusted',
  /** A day range inside the selected period that had no dynamic price before. */
  created = 'created',
  /** Days left exactly as they were, either outside the period or split off from an adjusted range. */
  unchanged = 'unchanged'
}

export type DateLike = Date | number | string;

export interface AdjustmentPeriod {
  from: DateLike;
  to: DateLike;
}

export interface CustomPriceAdjustmentInput {
  /** The unit's current customPrices array. */
  customPrices: CustomPriceModel[];
  /** Periods the ecommerce team selected on the calendar. Overlapping periods are merged. */
  periods: AdjustmentPeriod[];
  /** Positive percentage, e.g. 20 for 20%. */
  percentage: number;
  direction: PriceAdjustmentDirection;
  /** The unit's base price in USD, used for days inside the period with no dynamic price yet. */
  basePriceInUSD: number;
}

export interface PriceAdjustmentPreviewRow {
  /** Start of day epoch (ms). */
  from: number;
  /** Start of day epoch (ms). */
  to: number;
  numberOfDays: number;
  previousPriceInUSD: number;
  priceInUSD: number;
  changeType: PriceAdjustmentChangeType;
}

export interface CustomPriceAdjustmentResult {
  /** The new customPrices array, sorted by start date, ready to be persisted. */
  customPrices: CustomPriceModel[];
  /** Row-by-row breakdown of the same array, for the confirmation preview. */
  rows: PriceAdjustmentPreviewRow[];
  summary: {
    adjusted: number;
    created: number;
    unchanged: number;
  };
}

interface WorkingEntry {
  from: number;
  to: number;
  priceInUSD: number;
  /** Untouched pass-through of the original row, so unchanged rows keep their exact stored values. */
  source?: CustomPriceModel;
  previousPriceInUSD: number;
  changeType: PriceAdjustmentChangeType;
  /**
   * True once a period has cut this entry out of a stored range. Derived rows must not
   * inherit the stored row's identifiers, since one stored range can yield three rows.
   */
  isDerived: boolean;
}

interface DayRange {
  from: number;
  to: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Normalizes anything the API or the calendar may hand us (Date, epoch number, epoch string,
 * ISO string) down to the local start of that day, which is the granularity dynamic prices use.
 */
export function toStartOfDay(value: DateLike): number {
  const numeric =
    value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Number(value);
  const timestamp = Number.isFinite(numeric) ? numeric : new Date(String(value)).getTime();

  if (!Number.isFinite(timestamp)) return NaN;

  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function addDays(startOfDayEpoch: number, days: number): number {
  const date = new Date(startOfDayEpoch);
  date.setDate(date.getDate() + days);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

const previousDay = (day: number) => addDays(day, -1);
const nextDay = (day: number) => addDays(day, 1);

function countDays(range: DayRange): number {
  return Math.round((range.to - range.from) / MS_PER_DAY) + 1;
}

function byFrom(a: DayRange, b: DayRange): number {
  return a.from - b.from;
}

/**
 * The factor an existing price is multiplied by. Never goes below zero, so a
 * "decrease by more than 100%" can't produce a negative nightly rate.
 */
export function getAdjustmentMultiplier(
  percentage: number,
  direction: PriceAdjustmentDirection
): number {
  const magnitude = Math.abs(Number(percentage) || 0) / 100;
  return direction === PriceAdjustmentDirection.decrease
    ? Math.max(0, 1 - magnitude)
    : 1 + magnitude;
}

function applyMultiplier(price: number, multiplier: number): number {
  return Math.max(0, Math.round(price * multiplier));
}

/**
 * Merges the selected periods so every day is adjusted at most once, even if the
 * caller passes overlapping or back-to-back ranges.
 */
export function mergePeriods(periods: AdjustmentPeriod[]): DayRange[] {
  const normalized = (periods || [])
    .map((period) => ({ from: toStartOfDay(period.from), to: toStartOfDay(period.to) }))
    .filter((period) => Number.isFinite(period.from) && Number.isFinite(period.to))
    .map((period) => (period.from <= period.to ? period : { from: period.to, to: period.from }))
    .sort(byFrom);

  return normalized.reduce((merged: DayRange[], period) => {
    const last = merged[merged.length - 1];
    // Ranges that touch or are only a day apart become one range.
    if (last && period.from <= nextDay(last.to)) {
      last.to = Math.max(last.to, period.to);
      return merged;
    }
    merged.push({ ...period });
    return merged;
  }, []);
}

function toWorkingEntry(customPrice: CustomPriceModel): WorkingEntry | undefined {
  const from = toStartOfDay(customPrice.from);
  const to = toStartOfDay(customPrice.to);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return undefined;

  const priceInUSD = Number(customPrice.priceInUSD) || 0;
  return {
    from: Math.min(from, to),
    to: Math.max(from, to),
    priceInUSD,
    previousPriceInUSD: priceInUSD,
    source: customPrice,
    changeType: PriceAdjustmentChangeType.unchanged,
    isDerived: false
  };
}

/**
 * The parts of `period` that no entry in `covered` already accounts for.
 */
function findUncoveredRanges(period: DayRange, covered: DayRange[]): DayRange[] {
  const gaps: DayRange[] = [];
  let cursor = period.from;

  [...covered].sort(byFrom).forEach((range) => {
    if (range.from > cursor) {
      gaps.push({ from: cursor, to: previousDay(range.from) });
    }
    cursor = Math.max(cursor, nextDay(range.to));
  });

  if (cursor <= period.to) {
    gaps.push({ from: cursor, to: period.to });
  }

  return gaps;
}

/**
 * Applies one period to the working set: overlapping entries are split so only the days
 * inside the period move, and days the period covers that had no dynamic price get one
 * derived from the base price.
 */
function applyPeriod(
  entries: WorkingEntry[],
  period: DayRange,
  multiplier: number,
  basePriceInUSD: number
): WorkingEntry[] {
  const result: WorkingEntry[] = [];
  const covered: DayRange[] = [];

  entries.forEach((entry) => {
    if (entry.to < period.from || entry.from > period.to) {
      result.push(entry);
      return;
    }

    const insideFrom = Math.max(entry.from, period.from);
    const insideTo = Math.min(entry.to, period.to);

    const isSplit = entry.from < insideFrom || entry.to > insideTo;

    if (entry.from < insideFrom) {
      result.push({ ...entry, to: previousDay(insideFrom), isDerived: true });
    }

    result.push({
      ...entry,
      from: insideFrom,
      to: insideTo,
      priceInUSD: applyMultiplier(entry.priceInUSD, multiplier),
      previousPriceInUSD: entry.priceInUSD,
      changeType: PriceAdjustmentChangeType.adjusted,
      isDerived: entry.isDerived || isSplit
    });

    if (entry.to > insideTo) {
      result.push({ ...entry, from: nextDay(insideTo), isDerived: true });
    }

    covered.push({ from: insideFrom, to: insideTo });
  });

  // A unit with no base price gives us nothing to derive an uncovered day's price from.
  if (basePriceInUSD > 0) {
    findUncoveredRanges(period, covered).forEach((gap) => {
      result.push({
        from: gap.from,
        to: gap.to,
        priceInUSD: applyMultiplier(basePriceInUSD, multiplier),
        previousPriceInUSD: basePriceInUSD,
        changeType: PriceAdjustmentChangeType.created,
        isDerived: true
      });
    });
  }

  return result.sort(byFrom);
}

/**
 * Scales the display currency price alongside the USD price. The backend recomputes
 * `price` from `priceInUSD` and the current USD rate on save, so this only keeps the
 * table honest until the response comes back.
 */
function scaleDisplayPrice(source: CustomPriceModel | undefined, multiplier: number): string {
  const price = Number(source?.price);
  if (!source || !Number.isFinite(price) || price <= 0) return '';
  return String(Math.max(0, Math.round(price * multiplier)));
}

function toCustomPrice(entry: WorkingEntry, multiplier: number): CustomPriceModel {
  if (!entry.isDerived && entry.changeType === PriceAdjustmentChangeType.unchanged && entry.source) {
    // A stored range no period touched keeps its values, and its identifiers, verbatim.
    return entry.source;
  }

  // A stored range can yield up to three rows, so no derived row may carry the stored
  // row's channel manager id or mongo subdocument id along with it.
  const { id, _id, ...carriedOver } = (entry.source || {}) as CustomPriceModel & {
    id?: string;
    _id?: string;
  };

  return {
    ...carriedOver,
    from: entry.from,
    to: entry.to,
    price:
      entry.changeType === PriceAdjustmentChangeType.adjusted
        ? scaleDisplayPrice(entry.source, multiplier)
        : '',
    priceInUSD: String(entry.priceInUSD)
  };
}

/**
 * Increases or decreases a unit's dynamic prices by a percentage over the selected periods.
 *
 * Existing ranges are respected day by day: a range that only partly falls inside a
 * selected period is split, so the days outside it keep the price they had. Days the
 * period covers that had no dynamic price get a new one derived from the base price.
 */
export function adjustCustomPrices({
  customPrices,
  periods,
  percentage,
  direction,
  basePriceInUSD
}: CustomPriceAdjustmentInput): CustomPriceAdjustmentResult {
  const multiplier = getAdjustmentMultiplier(percentage, direction);
  const mergedPeriods = mergePeriods(periods);

  const entries = mergedPeriods.reduce(
    (working, period) => applyPeriod(working, period, multiplier, Number(basePriceInUSD) || 0),
    (customPrices || [])
      .map(toWorkingEntry)
      .filter((entry): entry is WorkingEntry => !!entry)
      .sort(byFrom)
  );

  const rows: PriceAdjustmentPreviewRow[] = entries.map((entry) => ({
    from: entry.from,
    to: entry.to,
    numberOfDays: countDays(entry),
    previousPriceInUSD: entry.previousPriceInUSD,
    priceInUSD: entry.priceInUSD,
    changeType: entry.changeType
  }));

  return {
    customPrices: entries.map((entry) => toCustomPrice(entry, multiplier)),
    rows,
    summary: {
      adjusted: rows.filter((row) => row.changeType === PriceAdjustmentChangeType.adjusted).length,
      created: rows.filter((row) => row.changeType === PriceAdjustmentChangeType.created).length,
      unchanged: rows.filter((row) => row.changeType === PriceAdjustmentChangeType.unchanged).length
    }
  };
}
